import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import { ACTIONS, TOOLTIP_DELAY } from "../../../common/Constants";
import { ACTIVITY_TYPE, ACTIVITY_TYPE_ICONS, API_DELIMITER, ERROR_MESSAGES } from "../../../../utils/constants";
import { useExportComplianceActivities, useGetAllComplianceActivities } from "../../../../backend/compliance";
import { hasUserAccess } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import Icon from "../../../common/Icon";
import Table, { CellTmpl, DEFAULT_PAYLOAD, TitleTmpl, reactFormatter } from "../../../common/Table";
import styles from "./Styles.module.css";
import ComplianceActivityDetails from "./ComplianceActivityDetails";
import {
    COMPLIANCE_ACTIVITY_INDICATION,
    ComplianceActivityStatus,
    ComplianceStatusMapping, setUserDetailsInFilters
} from "../../../../constants/Compliance.constants";
import { get } from "../../../../backend/request";
import { download, downloadWithPresignedUrl, downloadFileContent, preventDefault } from "../../../../utils/common";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import PageLoader from "../../../shared/PageLoader";
import * as api from "../../../../backend/request";
import OverlayTrigger from "react-bootstrap/OverlayTrigger"
import Tooltip from 'react-bootstrap/Tooltip';

const INCLUDES_ASSOCIATIONS = ['associate_company', 'company', 'location', 'act', 'rule', 'activity', 'compliance_manager', 'department', 'vertical', 'act_state_mapping', 'compliance_owner'];
const INCLUDE_ASSOCIATION_COLUMNS = {
    associate_company: {
        only: ['id', 'name', 'code']
    },
    company: {
        only: ['id', 'name', 'code']
    },
    location: {
        only: ['id', 'name', 'code']
    },
    act: {
        include: {
            law: {
                only: ['id', 'name', 'description']
            }
        }
    },
    rule: {
        only: ['id', 'name', 'rule_no', 'section_no', 'description']
    },
    activity: {
        only: ['id', 'name', 'activity_type', 'periodicity']
    },
    compliance_manager: {
        only: ['id', 'name']
    },
    department: {
        only: ['id', 'name']
    },
    vertical: {
        only: ['id', 'name']
    },
    act_state_mapping: {
        include: ['rule_compliance_detail']
    },
    compliance_owner: {
        only: ['id', 'name']
    }
}


const SortFields: any = {
    'month': 'start_date',
    'act.name': 'act_name',
    'rule.name': 'rule_name',
    'activity.name': 'activity_name',
    'associate_company.name': 'associate_company_name',
    'location.name': 'location_name',
    'activity.activity_type': 'activity_activity_type',
    'veritical.name': 'vertical_name'
};

function ComplianceOwnerActivities({ filters, handleCounts, refetchStats }: any) {
    const isEscalationManager = hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD);
    const [activity, setActivity] = useState<any>();
    const [action, setAction] = useState(ACTIONS.NONE);
    const [data, setData] = useState<any>();
    const [params, setParams] = useState<any>();
    const [payload, setPayload] = useState<any>(null);
    const payloadRef: any = useRef();
    payloadRef.current = payload;
    const { activities, total, statusCount, isFetching, refetch } = useGetAllComplianceActivities({...payload, include: INCLUDE_ASSOCIATION_COLUMNS, includes: INCLUDES_ASSOCIATIONS}, enableActivities());
    const { exportComplianceActivities, exporting } = useExportComplianceActivities((response: any) => {
        downloadFileContent({
            name: 'Compliance Activities.xlsx',
            type: response.headers['content-type'],
            content: response.data
        });
    }, () => {
        toast.error(ERROR_MESSAGES.DEFAULT);
    });

    function enableActivities() {
        const hasStartDate = hasFilters(null, 'start_date_lteq');
        if (isEscalationManager) {
            const hasCompany = Boolean((filters || {}).company_id_eq) || Boolean(((filters || {}).company_id_in || []).length);
            return hasStartDate && hasCompany;
        }
        return hasStartDate;
    }

    function hasFilters(ref: any, field = 'company_id_eq') {
        const _filters = (ref ? ref.current : { ...(payloadRef.current || {}) }.filters) || {};
        const column = _filters[field];
        return Boolean(column);
    }

    function dismissAction(refresh = false) {
        setAction(ACTIONS.NONE);
        setActivity(null);
        if (refresh) {
            refetch();
            refetchStats();
        }
    }

    function MonthTmpl({ cell }: any) {
        const row = cell.getData();
        return (
            <>{row.month} ({row.year})</>
        )
    }
    function LocationTmpl({ cell }: any) {
        const cellData = cell.getData();
        const { company, associate_company, location } = cellData;
        return (
            <div className="d-flex align-items-center h-100 w-auto">
                <OverlayTrigger overlay={<Tooltip>{location.name}</Tooltip>} rootClose={true}
                    placement="bottom" delay={{ show: TOOLTIP_DELAY } as any}>
                    <div className="ellipse two-lines">{company.code}-{associate_company.code}-{location.code}</div>
                </OverlayTrigger>
            </div>
        )
    }

    function DueDateTmpl({ cell }: any) {
        const value = cell.getValue();
        return (
            <span className="text-warn" >{dayjs(value).format('DD-MM-YYYY')}</span>
        )
    }

    function ActualCompletionDateTmpl({ cell }: any) {
        const row = cell.getData();
        const status = row.status;   //  get status from row
        const value = cell.getValue();
        // Hide date if rejected
        if (status === ComplianceActivityStatus.REJECTED) {
            return <span>-</span>;
        }
        const d = value && dayjs(value);
        const display =
            d && d.isValid() && d.year() > 100
                ? d.format('DD-MM-YYYY')
                : '';

        return <span>{display}</span>;
    }

    function FormStatusTmpl({ cell }: any) {
        const status = cell.getValue();
        return (
            <div className="d-flex align-items-center position-relative">
                <span className="ellipse" style={{ color: COMPLIANCE_ACTIVITY_INDICATION[status] || '' }}>{ComplianceStatusMapping[status] || status}</span>
            </div>
        )
    }

    function FormIndicationTmpl({ cell, row }: any) {
        const status = cell.getValue();
        return (
            <div className="d-flex align-items-center">
                <Icon name="circle" style={{ color: COMPLIANCE_ACTIVITY_INDICATION[status] || '' }} text={ComplianceStatusMapping[status]} />
            </div>
        )
    }

    function DownloadFormTmpl({ cell }: any) {
        const row = cell.getData();
        return (
            <div className="d-flex align-items-center">
                <Icon name="download" className={styles.downloadIcon} text={'Download Form'} data={row} action={downloadForm} />
            </div>
        )
    }

    function ActionColumnElements({ cell }: any) {
        const row = cell.getData();
        const hasAccess = hasUserAccess(USER_PRIVILEGES.OWNER_ACTIVITIES_SUBMIT) || hasUserAccess(USER_PRIVILEGES.MANAGER_ACTIVITIES_REVIEW);

        return (
            <div className="d-flex flex-row align-items-center position-relative">
                <Icon className="me-3" type="button" name={hasAccess ? "pencil" : "eye"} text={hasAccess ? "Edit" : "View"} data={row} action={handleEdit} />
            </div>
        )
    }

    function ActionHeaderTmpl() {
        return (
            <div className={`nav-item dropdown ${styles.tableActions}`}>
                <div className="nav-link text-white" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                    <FontAwesomeIcon icon={faEllipsisV} />
                </div>
                <div className="dropdown-menu">
                    <a className="dropdown-item" href="/" onClick={handleExport}>Export</a>
                    {/* removing this tempororily till api is ready */}
                     <a className="dropdown-item" href="/" onClick={bulkFileDownloadthroughZip}>All Files Download</a> 
                    {/* <a className="dropdown-item" href="/" onClick={handleAddNotice}>Add Notice</a> */}
                </div>
            </div>
        )
    }

    function bulkFileDownloadthroughZip(event: any) {
        event.preventDefault();

        const complianceIds = ((data || {}).data || [])
            .filter((item: { id: string }) => item.id)
            .map((item: { id: string }) => item.id);

        if (complianceIds.length > 0) {
            // API call to get the response with the x-uploaded-url header
            api.post('/api/v1/compliance_details/bulk_download', {compliance_ids: complianceIds}, {
                responseType: 'arraybuffer',
                headers: { 'Content-Type': 'application/zip' }
            }).then(({data}: any) => {
                let respo = (data || { data: {} }).data;
                if(respo.success) {
                    const urlMatch = respo.file_url;
                    if (urlMatch) {
                        const zipFileUrl = decodeURIComponent(urlMatch);
                        // Directly trigger download from the extracted URL
                        downloadWithPresignedUrl(`ComplianceActivities_${new Date().toISOString().split('T')[0]}.zip`, zipFileUrl);
                        toast.success("Zip FIle Downloaded");
                    }
                } else {
                    toast.warning(respo.error);
                }
            }).catch((error: any) => {

                let errorMessage = "Failed to download the ZIP file.";
                if (error.response.data) {
                    try {
                        const decoder = new TextDecoder("utf-8");
                        const decoded = decoder.decode(error.response.data);
                        const parsed = JSON.parse(decoded);
                        errorMessage = parsed.error || parsed.message || errorMessage;
                    } catch (e) {
                        console.error("Error downloading the file:", error);
                        toast.warning("Failed to download the ZIP file.");
                    }
                }

                toast.warning(error);
            });
        } else {
            toast.warning("There are no files available.");
        }
    }



    function ActivityTypeTmpl({ cell }: any) {
        const value = cell.getValue() || ACTIVITY_TYPE.AUDIT;
        return (
            <div className="d-flex flex-row align-items-center justify-content-center position-relative">
                <Icon name={ACTIVITY_TYPE_ICONS[value]} text={value} />
            </div>
        )
    }

    const columns = [
        {
            title: "",
            width: 30,
            headerSort: false,
            resizable: false,
            formatter: reactFormatter(<ActionColumnElements />),
        },
        {
            title: "Actions", width: 80, field: "status",
            headerSort: false,
            resizable: false,
            formatter: reactFormatter(
                <FormIndicationTmpl />
            )
        },
        {
            title: "", width: 30, field: "download",
            headerSort: false,
            resizable: false,
            formatter: reactFormatter(<DownloadFormTmpl />)
        },
        {
            title: "Month (Year)", field: "month", width: 180,
            resizable: false,
            formatter: reactFormatter(<MonthTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Owner", field: "compliance_owner.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1, minWidth: 140,
            visible: hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) || hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD)
        },
        {
            title: "Manager", field: "compliance_manager.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1, minWidth: 140,
            visible: hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD) || hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD)
        },
        {
            title: "Vertical", field: "vertical.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1, minWidth: 140
        },
        {
            title: "Department", field: "department.name",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1, minWidth: 140
        },
        {
            title: "Location", field: "location", headerSort: false,
            formatter: reactFormatter(<LocationTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1, minWidth: 200
        },
        {
            title: "Act", field: "act.name", widthGrow: 2, minWidth: 140, maxWidth: 300,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Rule", field: "rule.name", widthGrow: 2, minWidth: 140, maxWidth: 300,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Activity", field: "activity.name", minWidth: 140, maxWidth: 300,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 2
        },
        {
            title: "Activity Type", field: "activity.activity_type",
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            minWidth: 140, maxWidth: 200
        },
        {
            title: "Due Date", field: "due_date", width: 120,
            formatter: reactFormatter(<DueDateTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Actual Completion Date", field: "audited_date", width: 180,
            formatter: reactFormatter(<ActualCompletionDateTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Evidence Status", field: "status", width: 160,
            formatter: reactFormatter(<FormStatusTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        }
    ];

    function rowFormatter(row: any) {
        const data = row.getData();
        const element = row.getElement();
        if (data.isNotice) {
            element.classList.add('notice-activity');
        }
    }

    const [tableConfig] = useState({
        paginationMode: 'remote',
        ajaxRequestFunc,
        columns,
        rowHeight: 'auto',
        minHeight: '100%',
        selectable: false,
        paginate: true,
        bufferSpacing: 20,
        initialSort: [ 'month desc' ],
        rowFormatter
    });

    function formatApiResponse(params: any, list: any[], totalRecords: number) {
        const { per, page } = params || {};
        const tdata = {
            data: list,
            total: totalRecords,
            last_page: Math.ceil(totalRecords / (per || 1)) || 1,
            page: page || 1
        };
        setData(tdata);
        return tdata;
    }

    function ajaxRequestFunc(url: any, config: any, params: any) {
        const { field, dir } = (params.sort || [])[0] || {};
        const _params = {
            page: params.page,
            per: params.size,
            sorts: [`${SortFields[field] || field || 'due_date'} ${dir || 'desc'}`],
            include: INCLUDE_ASSOCIATION_COLUMNS,
            includes: INCLUDES_ASSOCIATIONS
        };
        setParams(_params);
        const _payload = { ...DEFAULT_PAYLOAD, ...payloadRef.current };
        const _filters = _payload.filters || {};
        setPayload({
            ...DEFAULT_PAYLOAD,
            ..._params,
            filters: setUserDetailsInFilters(_filters)
        });
        return Promise.resolve(formatApiResponse(params, activities, total));
    }

    function handlePageNav(_pagination: any) {
        const _params = { ...params, ..._pagination };
        setParams({ ..._params });
        setPayload({ ...payload, ..._params })
    }

    function handleEdit(_activity: any) {
        setActivity(_activity)
        setAction(ACTIONS.EDIT);
    }

    function handleCancel(refresh = false) {
        setAction(ACTIONS.NONE);
        if (refresh) {
            refetch();
        }
    }

    function handleExport(event: any) {
        event.preventDefault();
        if (!total) {
            toast.warn('There are no compliance activities available for the selected filter criteria.');
            return;
        }
        exportComplianceActivities({...payloadRef.current, page: 1, per: total});
    }

    function handleAddNotice(event: any) {
        preventDefault(event);
    }

    function downloadForm(activity: any) {
        if (activity.act_state_mapping.file_path) {
            downloadWithPresignedUrl(activity.act_state_mapping.file_name, activity.act_state_mapping.file_path)
        } else {
            toast.warn('No files available');
        }
    }

    useEffect(() => {
        if (filters) {
            const _params = { ...params, page: 1 };
            setParams({ ..._params });
            const _payload = {
                ...DEFAULT_PAYLOAD,
                sorts: [ 'start_date desc' ],
                ...payload
            };
            setPayload({
                ..._payload, filters: setUserDetailsInFilters({...filters}), ..._params
            });
        }
    }, [filters]);

    useEffect(() => {
        if (!isFetching && payload) {
            setData(formatApiResponse(params, activities, total));
            handleCounts(statusCount);
        }
    }, [isFetching])

    return (
        <>
            <div className="d-flex flex-column overflow-hidden">
                <div className={`card shadow ${styles.tableWrapper}`}>
                    <ActionHeaderTmpl />
                    <div className={styles.flexibleContainer}>
                        <Table data={data} options={tableConfig} isLoading={isFetching} onPageNav={handlePageNav} />
                    </div>
                </div>
            </div>
            {
                action === ACTIONS.EDIT && Boolean(activity) &&
                <ComplianceActivityDetails data={activity} onCancel={dismissAction} onSubmit={() => dismissAction(true)} />
            }
            {
                exporting && <PageLoader />
            }
        </>
    );

}

export default ComplianceOwnerActivities;